import { useState } from 'react';
import type { FC } from 'react';
import classnames from 'classnames';
import { CssTransition } from 'sento-transition';

import type { CheckboxStatus } from 'app/components/Checkbox';
import Checkbox from 'app/components/Checkbox';
import Button from 'app/components/Button/ResetedButton';
import VideogameIcon from 'app/components/VideogameIcon';
import Icon from 'app/components/Icon';
import arrowIcon from 'app/icons/arrow.svg';
import type { EventTreeVideogameValue, EventTreeChangeAction } from 'app/pages/EventsPage/types';

import style from './style.scss';
import EventTreeSerie from './EventTreeSerie';

interface Props {
  value: EventTreeVideogameValue;
  dispatchChange: (action: EventTreeChangeAction) => void;
}

const nbSelectedClassNames = {
  enter: style.nbSelected_enter,
  enterActive: style.nbSelected_enterActive,
  exit: style.nbSelected_exit,
  exitActive: style.nbSelected_exitActive
};

function getCheckboxStatus(value: EventTreeVideogameValue): CheckboxStatus {
  if (value.selected) {
    return 'checked';
  }

  const someTournamentsSelected = value.series.some(s => s.tournaments.some(t => t.selected));
  if (someTournamentsSelected) {
    return 'indeterminate';
  }

  return 'unchecked';
}

function getNbSelectedTournaments(value: EventTreeVideogameValue): number {
  const allVideogameTournaments = value.series.map(serie => serie.tournaments).flat();
  const nbSelectedTournaments = allVideogameTournaments.filter(t => t.selected).length;

  if (value.selected && nbSelectedTournaments === 0) {
    return allVideogameTournaments.length;
  }

  return nbSelectedTournaments;
}

const EventTreeVideogame: FC<Props> = ({ value, dispatchChange }) => {
  const [expanded, setExpanded] = useState(false);
  const nbSelectedTournaments = getNbSelectedTournaments(value);

  const toggleExpanded = () => setExpanded(e => !e);

  const handleChange = () => {
    dispatchChange({ type: 'EVENT_TREE_TOGGLE_VIDEOGAME', payload: value.key });
  };

  const labelClassName = classnames(style.label, {
    [style.label_selected]: nbSelectedTournaments > 0
  });
  const arrowClassName = classnames(style.arrow, {
    [style.arrow_expanded]: expanded
  });

  return (
    <div className={style.container}>
      <div className={style.line}>
        <label className={labelClassName}>
          <Checkbox
            className={style.checkbox}
            status={getCheckboxStatus(value)}
            disabled={value.series.length === 0}
            onChange={handleChange}
          />
          <VideogameIcon className={style.videogameIcon} size={22} slug={value.slug} />
          <span className={style.videogameName}>{value.label}</span>
          <span className={style.nbSelected}>
            <CssTransition
              displayed={nbSelectedTournaments > 0}
              classNames={nbSelectedClassNames}
              timeout={200}
            >
              <span>{nbSelectedTournaments}</span>
            </CssTransition>
          </span>
        </label>
        {value.series.length > 0 && (
          <Button className={style.expandButton} onClick={toggleExpanded}>
            <Icon className={arrowClassName} href={arrowIcon} />
          </Button>
        )}
      </div>
      {expanded && (
        <div className={style.seriesContainer}>
          {value.series.map(serie => (
            <EventTreeSerie key={serie.id} value={serie} dispatchChange={dispatchChange} />
          ))}
        </div>
      )}
    </div>
  );
};

EventTreeVideogame.displayName = 'EventTreeVideogame';

export default EventTreeVideogame;
